export interface ToneSpec {
  frequencyHz: number
  beepDurationSeconds: number
  gapSeconds: number
  repeat: number
  peakGain: number
}

export const MESSAGE_TONE: ToneSpec = {
  frequencyHz: 880,
  beepDurationSeconds: 0.5,
  gapSeconds: 0,
  repeat: 1,
  peakGain: 0.3,
}

export const INCOMING_CALL_TONE: ToneSpec = {
  frequencyHz: 660,
  beepDurationSeconds: 0.35,
  gapSeconds: 0.15,
  repeat: 3,
  peakGain: 0.3,
}

// Played while the agent is already on a call, so it has to be clearly
// different from the message beep and quiet enough not to drown the caller.
export const CALL_WAITING_TONE: ToneSpec = {
  frequencyHz: 440,
  beepDurationSeconds: 0.15,
  gapSeconds: 0.12,
  repeat: 2,
  peakGain: 0.15,
}

// A conversation handed over by the bot — two short, higher beeps so the
// agent can tell it apart from a plain new message without looking.
export const TRANSFER_TONE: ToneSpec = {
  frequencyHz: 1046,
  beepDurationSeconds: 0.16,
  gapSeconds: 0.08,
  repeat: 2,
  peakGain: 0.3,
}

const TONE_ATTACK_SECONDS = 0.05
const TONE_SILENT_GAIN = 0.001

let sharedAudioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  try {
    const AudioContextClass =
      window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return null

    if (!sharedAudioCtx) {
      sharedAudioCtx = new AudioContextClass()
    }
    if (sharedAudioCtx.state === 'suspended') {
      void sharedAudioCtx.resume()
    }
    return sharedAudioCtx
  } catch {
    return null
  }
}

export function playTone(tone: ToneSpec): void {
  const ctx = getAudioContext()
  if (!ctx) return

  try {
    for (let i = 0; i < tone.repeat; i++) {
      const start = ctx.currentTime + i * (tone.beepDurationSeconds + tone.gapSeconds)
      const end = start + tone.beepDurationSeconds
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = tone.frequencyHz
      gain.gain.setValueAtTime(0, start)
      gain.gain.linearRampToValueAtTime(tone.peakGain, start + TONE_ATTACK_SECONDS)
      gain.gain.exponentialRampToValueAtTime(TONE_SILENT_GAIN, end)
      osc.start(start)
      osc.stop(end)
    }
  } catch {
    // AudioContext not usable — ignore
  }
}
